import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MonthCalendar from './MonthCalendar';
import TrainingPlanTable, { DEFAULT_PLAN_ROWS } from './TrainingPlanTable';

const API_URL = '/api';

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatDisplayDateEn(dateStr) {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${MONTHS_EN[month - 1]} ${day}, ${year}`;
    }
  } catch {
    // fallback
  }
  return dateStr;
}

function parseWorkoutIntoPlan(w) {
  if (!w) return null;
  let rows = [];
  let comments = [];
  let trainingNum = 1;
  if (typeof w.content === 'object' && w.content !== null) {
    if (Array.isArray(w.content)) {
      rows = w.content;
    } else if (w.content.rows) {
      rows = w.content.rows;
      comments = w.content.comments || [];
      if (w.content.trainingNumber) trainingNum = Number(w.content.trainingNumber);
    }
  } else if (typeof w.content === 'string' && w.content) {
    try {
      const parsed = JSON.parse(w.content);
      if (Array.isArray(parsed)) {
        rows = parsed;
      } else if (parsed && parsed.rows) {
        rows = parsed.rows;
        comments = parsed.comments || [];
        if (parsed.trainingNumber) trainingNum = Number(parsed.trainingNumber);
      }
    } catch (e) {
      // simple text
    }
  }

  // Fallback from title if title contains e.g. "Workout 2"
  if (trainingNum === 1 && w.title) {
    const match = w.title.match(/Workout\s*(\d+)/i);
    if (match) {
      trainingNum = Number(match[1]);
    }
  }

  const rawSt = w.status || 'SCHEDULED';
  const isCompletedVal = rawSt === 'DONE' || rawSt === 'completed';
  const isSkippedVal = rawSt === 'SKIPPED' || rawSt === 'skipped';

  return {
    id: w.id,
    title: w.title,
    trainingNumber: trainingNum,
    status: isCompletedVal ? 'completed' : isSkippedVal ? 'skipped' : 'pending',
    rawStatus: rawSt,
    userComment: w.userComment || w.user_comment || '',
    rows: rows.length > 0 ? rows : DEFAULT_PLAN_ROWS,
    comments: w.comments || comments
  };
}

function ClientDetail({ clientId: propClientId, auth }) {
  const { clientId: paramClientId, dateStr: paramDateStr } = useParams();
  const clientId = propClientId || paramClientId;
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trainerTemplates, setTrainerTemplates] = useState([]);

  const selectedDate = paramDateStr || null;

  const storageKey = `training_plans_client_${clientId}`;

  const [clientPlans, setClientPlans] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Error loading client plans from localStorage:', e);
    }
    return {};
  });

  useEffect(() => {
    fetchClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [clientRes, workoutsRes, templatesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/trainer/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${auth?.token}` },
        }),
        axios.get(`${API_URL}/trainer/clients/${clientId}/workouts`, {
          headers: { Authorization: `Bearer ${auth?.token}` },
        }),
        axios.get(`${API_URL}/trainer/templates`, {
          headers: { Authorization: `Bearer ${auth?.token}` },
        })
      ]);

      if (clientRes.status === 'fulfilled') {
        setClient(clientRes.value.data);
      } else {
        const errorDetail = clientRes.reason?.response?.data?.error || clientRes.reason?.message || 'Client not found or access denied';
        setError(errorDetail);
        setLoading(false);
        return;
      }

      if (templatesRes.status === 'fulfilled' && Array.isArray(templatesRes.value.data)) {
        setTrainerTemplates(templatesRes.value.data);
      }

      // Populate workouts map if fetched
      if (workoutsRes.status === 'fulfilled' && Array.isArray(workoutsRes.value.data)) {
        const plansMap = {};
        workoutsRes.value.data.forEach(w => {
          if (w.dateKey) {
            const plan = parseWorkoutIntoPlan(w);
            if (plan) {
              plansMap[w.dateKey] = plan;
            }
          }
        });
        setClientPlans(plansMap);
        localStorage.setItem(storageKey, JSON.stringify(plansMap));
      }

      setError(null);
    } catch (err) {
      console.error('[ClientDetail] Error loading client:', err.response?.data || err);
      setError('Error loading client data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Real-time SSE updates for trainer client detail view (comments, workouts, status)
  useEffect(() => {
    const handleCommentAdded = (e) => {
      const data = e.detail;
      if (!data?.comment || !data?.workout_date) return;
      if (data.workout_user_id && Number(data.workout_user_id) !== Number(clientId)) {
        return;
      }
      setClientPlans((prev) => {
        const plan = prev[data.workout_date];
        if (!plan) return prev;
        const currentComments = plan.comments || [];
        if (currentComments.some((c) => c.id === data.comment.id)) return prev;
        const updated = {
          ...prev,
          [data.workout_date]: {
            ...plan,
            id: plan.id || data.workout_id,
            comments: [...currentComments, data.comment]
          }
        };
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });
    };

    const handleWorkoutUpdated = (e) => {
      const data = e.detail;
      if (!data?.workout || !data?.workout_date) return;
      if (data.client_id && Number(data.client_id) !== Number(clientId)) {
        return;
      }
      const parsedPlan = parseWorkoutIntoPlan(data.workout);
      if (!parsedPlan) return;

      setClientPlans((prev) => {
        const existing = prev[data.workout_date] || {};
        const mergedComments = data.workout.comments || existing.comments || [];
        const updated = {
          ...prev,
          [data.workout_date]: {
            ...existing,
            ...parsedPlan,
            comments: mergedComments
          }
        };
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });
    };

    const handleWorkoutDeleted = (e) => {
      const data = e.detail;
      if (data?.client_id && Number(data.client_id) !== Number(clientId)) {
        return;
      }
      if (data?.workout_date) {
        setClientPlans((prev) => {
          const updated = { ...prev };
          delete updated[data.workout_date];
          try {
            localStorage.setItem(storageKey, JSON.stringify(updated));
          } catch (_) {}
          return updated;
        });
      }
    };

    window.addEventListener('tp:comment_added', handleCommentAdded);
    window.addEventListener('tp:workout_updated', handleWorkoutUpdated);
    window.addEventListener('tp:workout_deleted', handleWorkoutDeleted);
    return () => {
      window.removeEventListener('tp:comment_added', handleCommentAdded);
      window.removeEventListener('tp:workout_updated', handleWorkoutUpdated);
      window.removeEventListener('tp:workout_deleted', handleWorkoutDeleted);
    };
  }, [clientId, storageKey]);

  const handleOpenDayPlan = useCallback((dateKey) => {
    navigate(`/trainer/client/${clientId}/workout/${dateKey}`);
  }, [clientId, navigate]);

  const handleSaveDayPlan = useCallback(async (dateKey, planData) => {
    setClientPlans(prev => {
      const updated = {
        ...prev,
        [dateKey]: planData
      };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });

    try {
      let statusEnum = 'SCHEDULED';
      if (planData.status === 'completed' || planData.status === 'DONE') statusEnum = 'DONE';
      else if (planData.status === 'skipped' || planData.status === 'SKIPPED') statusEnum = 'SKIPPED';

      const res = await axios.post(`${API_URL}/trainer/clients/${clientId}/workouts`, {
        date: dateKey,
        title: planData.title || `Workout ${planData.trainingNumber || 1} - ${formatDisplayDateEn(dateKey)}`,
        content: {
          trainingNumber: planData.trainingNumber || 1,
          rows: planData.rows || []
        },
        status: statusEnum,
        userComment: planData.userComment || ''
      }, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });

      setClientPlans(prev => {
        const updated = {
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            id: res.data.id,
            comments: res.data.comments || prev[dateKey]?.comments || []
          }
        };
        localStorage.setItem(storageKey, JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      console.warn('Backend sync warning:', err);
    }
  }, [clientId, auth?.token, storageKey]);

  const handleAddWorkoutComment = useCallback(async (commentText) => {
    const plan = clientPlans[selectedDate];
    if (!plan?.id) {
      throw new Error('Please wait for the workout to save before posting a comment.');
    }

    const res = await axios.post(`${API_URL}/workouts/${plan.id}/comments`, {
      text: commentText
    }, {
      headers: { Authorization: `Bearer ${auth?.token}` }
    });

    setClientPlans(prev => {
      const currentPlan = prev[selectedDate] || {};
      const currentComments = currentPlan.comments || [];
      if (currentComments.some(c => c.id === res.data.id)) {
        return prev;
      }
      const updated = {
        ...prev,
        [selectedDate]: {
          ...currentPlan,
          comments: [...currentComments, res.data]
        }
      };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });

    return res.data;
  }, [auth?.token, clientPlans, selectedDate, storageKey]);

  const handleDeleteDayPlan = useCallback(async (dateKey) => {
    const planToDelete = clientPlans[dateKey];

    setClientPlans(prev => {
      const updated = { ...prev };
      delete updated[dateKey];
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });

    navigate(`/trainer/client/${clientId}`, { replace: true });

    try {
      if (planToDelete?.id) {
        await axios.delete(`${API_URL}/trainer/workouts/${planToDelete.id}`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
      } else {
        await axios.delete(`${API_URL}/trainer/clients/${clientId}/workouts?date=${dateKey}`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
      }
    } catch (err) {
      console.warn('Error deleting workout from server:', err);
    }
  }, [clientId, auth?.token, clientPlans, storageKey, navigate]);

  const handleCopyDayPlan = useCallback(async (targetDateKey, sourceDateKey) => {
    const sourcePlan = clientPlans[sourceDateKey];
    if (!sourcePlan) return;
    const clonedPlan = JSON.parse(JSON.stringify(sourcePlan));

    clonedPlan.status = 'pending';
    clonedPlan.rawStatus = 'SCHEDULED';
    clonedPlan.userComment = '';
    clonedPlan.comments = [];

    setClientPlans(prev => {
      const updated = {
        ...prev,
        [targetDateKey]: clonedPlan
      };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });

    try {
      const res = await axios.post(`${API_URL}/trainer/clients/${clientId}/workouts`, {
        date: targetDateKey,
        title: clonedPlan.title || `Workout ${clonedPlan.trainingNumber || 1} - ${formatDisplayDateEn(targetDateKey)}`,
        content: {
          trainingNumber: clonedPlan.trainingNumber || 1,
          rows: clonedPlan.rows || []
        },
        userComment: ''
      }, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });

      setClientPlans(prev => {
        const updated = {
          ...prev,
          [targetDateKey]: {
            ...clonedPlan,
            id: res.data.id,
            comments: []
          }
        };
        localStorage.setItem(storageKey, JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      console.error('Error saving copied workout plan:', err);
    }
  }, [clientId, auth?.token, storageKey, clientPlans]);

  const handleCopyFromTemplate = useCallback(async (targetDateKey, template) => {
    if (!template) return;
    let rows = [];
    if (template.content) {
      if (Array.isArray(template.content)) {
        rows = template.content;
      } else if (template.content.rows && Array.isArray(template.content.rows)) {
        rows = template.content.rows;
      }
    }

    const clonedPlan = {
      title: `${template.name} - ${formatDisplayDateEn(targetDateKey)}`,
      trainingNumber: 1,
      status: 'pending',
      rawStatus: 'SCHEDULED',
      userComment: '',
      comments: [],
      rows: rows.length > 0 ? rows : DEFAULT_PLAN_ROWS
    };

    setClientPlans(prev => {
      const updated = {
        ...prev,
        [targetDateKey]: clonedPlan
      };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });

    try {
      const res = await axios.post(`${API_URL}/trainer/clients/${clientId}/workouts`, {
        date: targetDateKey,
        title: clonedPlan.title,
        content: {
          trainingNumber: 1,
          rows: clonedPlan.rows
        },
        userComment: ''
      }, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });

      setClientPlans(prev => {
        const updated = {
          ...prev,
          [targetDateKey]: {
            ...clonedPlan,
            id: res.data.id,
            comments: []
          }
        };
        localStorage.setItem(storageKey, JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      console.error('Error saving copied template workout plan:', err);
    }
  }, [clientId, auth?.token, storageKey]);

  if (loading) return <div className="loading">Loading...</div>;
  if (!client) return <div className="error">Client not found</div>;

  return (
    <div>
      {!selectedDate && (
        <div className="client-detail-name-wrapper">
          <button
            type="button"
            className="btn-cal-arrow btn-back-to-clients"
            onClick={() => navigate('/trainer')}
            title="Back to clients"
          >
            &lt;
          </button>
          <span className="client-detail-name-label">{client.name}</span>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {selectedDate ? (
        <TrainingPlanTable
          key={selectedDate}
          clientName={client.name}
          dateStr={selectedDate}
          initialData={clientPlans[selectedDate]}
          onSave={handleSaveDayPlan}
          onAddComment={handleAddWorkoutComment}
          onDeletePlan={handleDeleteDayPlan}
          onBack={() => {
            const parts = selectedDate.split('-');
            if (parts.length === 3) {
              const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
              if (!isNaN(d.getTime())) {
                sessionStorage.setItem('calendar_current_date', d.toISOString());
              }
            }
            navigate(`/trainer/client/${clientId}`);
          }}
          auth={auth}
          canEditPlan={true}
        />
      ) : (
        <MonthCalendar
          plans={clientPlans}
          templates={trainerTemplates}
          onOpenDayPlan={handleOpenDayPlan}
          onCopyPlan={handleCopyDayPlan}
          onCopyFromTemplate={handleCopyFromTemplate}
        />
      )}
    </div>
  );
}

export default ClientDetail;
